export function TermsOfServiceContent() {
  return (
    <div className="prose prose-sm max-w-none text-foreground">
      <h2 className="text-lg font-semibold">Terms of Use</h2>
      <p className="text-sm text-muted-foreground">
        Last updated: February 23, 2026
      </p>

      <p className="text-sm leading-relaxed mt-4">
        THESE TERMS OF USE (&ldquo;Agreement&rdquo;) are a binding agreement
        between you (&ldquo;Licensee,&rdquo; &ldquo;User,&rdquo; or
        &ldquo;you&rdquo;) and C2 Vault, LLC (&ldquo;Company,&rdquo;
        &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;).
      </p>
      <p className="text-sm leading-relaxed">
        This Agreement governs your access to and use of the C2 Vault website,
        software platform, and related services (collectively, the
        &ldquo;Application&rdquo;). The Application is licensed, not sold.
      </p>
      <p className="text-sm leading-relaxed font-semibold">
        BY ACCESSING, REGISTERING FOR, OR USING THE APPLICATION, YOU:
      </p>
      <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
        <li>Acknowledge that you have read and understand this Agreement;</li>
        <li>
          Represent that you are at least 18 years old and legally authorized to
          enter into this Agreement;
        </li>
        <li>Agree to be legally bound by its terms;</li>
        <li>
          If acting on behalf of a pharmacy or corporate entity, represent that
          you have authority to bind that entity;
        </li>
        <li>
          Represent that you or your employer are a registrant in good standing
          with the United States Drug Enforcement Administration
          (&ldquo;DEA&rdquo;) if using the Application to track controlled
          substances.
        </li>
      </ul>
      <p className="text-sm leading-relaxed font-semibold">
        If you do not agree, do not access or use the Application.
      </p>

      <h3 className="mt-6 text-base font-semibold">1. License Grant</h3>
      <p className="text-sm leading-relaxed">
        Subject to this Agreement and your active subscription, Company grants
        you a limited, non-exclusive, non-transferable, revocable license to
        access and use the Application solely for lawful business purposes
        related to pharmacy operations and controlled substance recordkeeping.
        Company retains all right, title, and interest in and to the Application
        and all related technology, including all software, source code, object
        code, algorithms, designs, documentation, trademarks, trade secrets, and
        other intellectual property rights. No rights are granted to Licensee
        except as expressly set forth in this Agreement. Any feedback,
        suggestions, enhancement requests, or recommendations provided by users
        may be used by Company without restriction or compensation.
      </p>

      <h3 className="mt-6 text-base font-semibold">
        2. Subscription and Payment
      </h3>
      <p className="text-sm leading-relaxed font-medium">
        (a) Subscription Plans
      </p>
      <p className="text-sm leading-relaxed">
        Access to the Application requires an active paid subscription. Company
        offers both monthly and annual subscription plans. Subscription fees and
        pricing are listed on the Company website and are subject to change with
        notice. Subscriptions renew automatically unless canceled prior to the
        renewal date.
      </p>
      <p className="text-sm leading-relaxed font-medium mt-3">
        (b) Payment Terms
      </p>
      <p className="text-sm leading-relaxed">
        All fees are billed in advance and are non-refundable except as
        expressly provided below. You are responsible for all applicable taxes.
        Failure to pay subscription fees may result in suspension or termination
        of access.
      </p>
      <p className="text-sm leading-relaxed font-medium mt-3">
        (c) Refund Policy
      </p>
      <p className="text-sm leading-relaxed font-medium">
        Monthly Subscriptions
      </p>
      <p className="text-sm leading-relaxed">
        Monthly subscription fees are non-refundable once charged. You may
        cancel at any time, and access will continue through the end of the
        current billing cycle.
      </p>
      <p className="text-sm leading-relaxed font-medium mt-2">
        Annual Subscriptions
      </p>
      <p className="text-sm leading-relaxed">
        Company may, at its sole discretion, provide a refund of the initial
        annual subscription fee if the Application experiences a material
        technical malfunction that substantially prevents use of its core
        functionality, and such issue is not resolved within thirty (30) days
        after written notice to Company. Refund requests must be submitted
        within thirty (30) days of the initial annual subscription charge.
      </p>
      <p className="text-sm leading-relaxed">
        Refunds do not apply to renewal terms, minor bugs, feature requests,
        dissatisfaction unrelated to core functionality, user error, internet
        outages, third-party service disruptions, regulatory actions, audit
        outcomes, or failure to achieve specific compliance results.
      </p>

      <h3 className="mt-6 text-base font-semibold">3. License Restrictions</h3>
      <p className="text-sm leading-relaxed">Licensee may not:</p>
      <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
        <li>Copy, reproduce, or distribute the Application</li>
        <li>Reverse engineer, decompile, or attempt to derive source code</li>
        <li>Modify or create derivative works</li>
        <li>Sell, sublicense, or transfer access</li>
        <li>Use the Application for unlawful purposes</li>
        <li>Share login credentials outside your organization</li>
      </ul>
      <p className="text-sm leading-relaxed">
        Violation may result in immediate termination.
      </p>

      <h3 className="mt-6 text-base font-semibold">4. Compliance Disclaimer</h3>
      <p className="text-sm leading-relaxed">
        C2 Vault is a recordkeeping and reporting assistance tool. The
        Application does NOT:
      </p>
      <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
        <li>Replace legal advice</li>
        <li>Guarantee compliance with DEA, state, or federal law</li>
        <li>Assume responsibility for pharmacy regulatory obligations</li>
        <li>Replace required physical inventory counts</li>
        <li>Replace required state or federal reporting systems</li>
      </ul>
      <p className="text-sm leading-relaxed mt-2">
        You remain solely responsible for:
      </p>
      <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
        <li>Accurate data entry</li>
        <li>Physical inventory verification</li>
        <li>Filing required DEA forms</li>
        <li>Maintaining compliance with all applicable laws</li>
      </ul>
      <p className="text-sm leading-relaxed">
        Company makes no representation that use of the Application alone
        ensures regulatory compliance.
      </p>

      <h3 className="mt-6 text-base font-semibold">5. Data Responsibility</h3>
      <p className="text-sm leading-relaxed">You are solely responsible for:</p>
      <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
        <li>Accuracy of all data entered</li>
        <li>Lawful handling of prescription information</li>
        <li>
          Ensuring no protected health information is entered unless compliant
          with HIPAA
        </li>
      </ul>
      <p className="text-sm leading-relaxed">
        Company is not responsible for errors resulting from inaccurate data
        entry.
      </p>

      <h3 className="mt-6 text-base font-semibold">6. Account Security</h3>
      <p className="text-sm leading-relaxed">You must:</p>
      <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
        <li>Maintain confidentiality of login credentials</li>
        <li>Restrict access to authorized personnel</li>
        <li>Notify Company of unauthorized access</li>
      </ul>
      <p className="text-sm leading-relaxed">
        Company is not liable for losses resulting from compromised credentials.
      </p>

      <h3 className="mt-6 text-base font-semibold">
        7. Data Storage, Backups, and Third-Party Infrastructure
      </h3>
      <p className="text-sm leading-relaxed">
        The Application stores data in encrypted cloud environments located
        within the United States. Company employs commercially reasonable
        security measures to protect data. However:
      </p>
      <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
        <li>No system is guaranteed to be 100% secure</li>
        <li>
          Company does not warrant that the Application will be free from
          security breaches or unauthorized access
        </li>
        <li>
          Company is not liable for data breaches, interruptions, delays, or
          data loss caused by third-party infrastructure providers, including
          but not limited to cloud hosting providers, payment processors,
          internet service providers, or other technology vendors
        </li>
      </ul>
      <p className="text-sm leading-relaxed">
        The Application may rely on third-party services for hosting, payment
        processing, authentication, or communications. Company does not control
        and is not responsible for the availability, performance, or reliability
        of such third-party services. Users are solely responsible for
        maintaining backups or copies of records if required under federal or
        state law.
      </p>

      <h3 className="mt-6 text-base font-semibold">
        8. HIPAA and Protected Health Information
      </h3>
      <p className="text-sm leading-relaxed">
        The Application is not intended to store or process patient names, dates
        of birth, addresses, diagnoses, or other protected health information as
        defined under the Health Insurance Portability and Accountability Act
        (&ldquo;HIPAA&rdquo;). Licensee agrees not to enter protected health
        information into the Application except to the limited extent necessary
        for prescription tracking using non-identifiable prescription reference
        numbers. Licensee remains solely responsible for compliance with HIPAA
        and all other privacy laws. Company does not assume the role of a
        Business Associate unless expressly agreed to in a separate written
        agreement signed by both parties.
      </p>

      <h3 className="mt-6 text-base font-semibold">9. Updates</h3>
      <p className="text-sm leading-relaxed">
        Company may modify, update, or discontinue features at its sole
        discretion. Updates may be automatic and are subject to this Agreement.
        Company is not obligated to maintain any specific feature indefinitely.
      </p>

      <h3 className="mt-6 text-base font-semibold">10. Force Majeure</h3>
      <p className="text-sm leading-relaxed">
        Company shall not be liable or responsible for any delay, failure in
        performance, or interruption of service resulting from causes beyond its
        reasonable control, including but not limited to acts of God, natural
        disasters, cyberattacks, internet outages, power failures, governmental
        actions, labor disputes, cloud hosting failures, or other events beyond
        Company&apos;s control.
      </p>

      <h3 className="mt-6 text-base font-semibold">
        11. Disclaimer of Warranties
      </h3>
      <p className="text-sm leading-relaxed font-semibold">
        THE APPLICATION IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS
        AVAILABLE.&rdquo; TO THE MAXIMUM EXTENT PERMITTED BY LAW, COMPANY
        DISCLAIMS ALL WARRANTIES, INCLUDING:
      </p>
      <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
        <li>MERCHANTABILITY</li>
        <li>FITNESS FOR A PARTICULAR PURPOSE</li>
        <li>NON-INFRINGEMENT</li>
        <li>ACCURACY OR RELIABILITY</li>
      </ul>
      <p className="text-sm leading-relaxed mt-2">Company does not warrant:</p>
      <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
        <li>That the Application will prevent audit findings</li>
        <li>That the Application will meet all regulatory requirements</li>
        <li>That the Application will operate without interruption</li>
      </ul>

      <h3 className="mt-6 text-base font-semibold">
        12. Limitation of Liability
      </h3>
      <p className="text-sm leading-relaxed font-semibold">
        TO THE MAXIMUM EXTENT PERMITTED BY LAW:
      </p>
      <p className="text-sm leading-relaxed">
        Company shall not be liable for any indirect, incidental, special,
        consequential, or punitive damages, including but not limited to
        regulatory fines, DEA enforcement actions, license suspension, business
        interruption, lost profits, or data loss, even if advised of the
        possibility of such damages.
      </p>
      <p className="text-sm leading-relaxed">
        In no event shall Company&apos;s total aggregate liability arising out
        of or related to this Agreement exceed the total subscription fees paid
        by Licensee to Company during the twelve (12) months preceding the event
        giving rise to the claim. The remedies set forth in this Agreement are
        the exclusive remedies available to Licensee.
      </p>

      <h3 className="mt-6 text-base font-semibold">13. Limitation on Claims</h3>
      <p className="text-sm leading-relaxed">
        Any claim, action, or proceeding arising out of or relating to this
        Agreement or the Application must be brought within one (1) year after
        the cause of action first arises. Any claim not brought within this time
        period is permanently barred.
      </p>

      <h3 className="mt-6 text-base font-semibold">14. Indemnification</h3>
      <p className="text-sm leading-relaxed">
        You agree to indemnify and hold harmless Company from any claims,
        losses, penalties, or damages arising from:
      </p>
      <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
        <li>Your misuse of the Application</li>
        <li>Regulatory violations</li>
        <li>Inaccurate data entry</li>
        <li>Breach of this Agreement</li>
      </ul>

      <h3 className="mt-6 text-base font-semibold">15. Term and Termination</h3>
      <p className="text-sm leading-relaxed">
        This Agreement remains in effect for as long as you access or use the
        Application or maintain an active subscription. Company may suspend or
        terminate your access to the Application immediately, with or without
        notice, if:
      </p>
      <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
        <li>Subscription fees are not paid when due</li>
        <li>You breach this Agreement</li>
        <li>You engage in unlawful, fraudulent, or improper activity</li>
        <li>Continued access poses legal, regulatory, or security risk</li>
      </ul>
      <p className="text-sm leading-relaxed mt-2">
        Upon termination or expiration of your subscription:
      </p>
      <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
        <li>Access to the Application will terminate immediately</li>
        <li>
          Licensee will have thirty (30) days from the date of termination to
          download its data, unless otherwise required by law
        </li>
        <li>
          After such thirty (30) day period, Company may permanently delete all
          account data without further notice
        </li>
      </ul>
      <p className="text-sm leading-relaxed">
        Company is not obligated to retain account data beyond this period and
        shall not be liable for deletion of data following termination. Licensee
        remains solely responsible for retaining and maintaining any records
        required under federal, state, or regulatory law, including controlled
        substance recordkeeping obligations.
      </p>

      <h3 className="mt-6 text-base font-semibold">16. Assignment</h3>
      <p className="text-sm leading-relaxed">
        Licensee may not assign or transfer this Agreement or any rights granted
        hereunder without the prior written consent of Company. Company may
        assign this Agreement without restriction, including in connection with
        a merger, acquisition, asset sale, or reorganization.
      </p>

      <h3 className="mt-6 text-base font-semibold">
        17. Dispute Resolution; Arbitration
      </h3>
      <p className="text-sm leading-relaxed">
        Any dispute, claim, or controversy arising out of or relating to this
        Agreement or the Application shall be resolved exclusively through
        binding arbitration administered by the American Arbitration Association
        in accordance with its Commercial Arbitration Rules. The arbitration
        shall take place in New York County, New York. Judgment on the award
        rendered by the arbitrator may be entered in any court of competent
        jurisdiction. The parties agree to waive any right to a trial by jury.
      </p>
      <p className="text-sm leading-relaxed font-semibold mt-2">
        Class Action Waiver:
      </p>
      <p className="text-sm leading-relaxed">
        All claims must be brought in an individual capacity, and not as a
        plaintiff or class member in any purported class, collective,
        consolidated, or representative proceeding. The arbitrator shall have no
        authority to hear or decide any class or representative action.
      </p>

      <h3 className="mt-6 text-base font-semibold">18. Governing Law</h3>
      <p className="text-sm leading-relaxed">
        This Agreement shall be governed by and construed in accordance with the
        laws of the State of New York, without regard to its conflict of law
        principles. Subject to the Dispute Resolution; Arbitration section
        above, any court proceedings permitted under this Agreement shall be
        brought exclusively in the state or federal courts located in New York
        County, New York, and the parties hereby consent to the personal
        jurisdiction and venue of such courts.
      </p>

      <h3 className="mt-6 text-base font-semibold">19. Changes to Agreement</h3>
      <p className="text-sm leading-relaxed">
        Company may update this Agreement at any time. Continued use constitutes
        acceptance of changes. If you do not agree to such an update, you must
        immediately stop using the Application.
      </p>

      <h3 className="mt-6 text-base font-semibold">20. Severability</h3>
      <p className="text-sm leading-relaxed">
        If any provision of this Agreement is found to be invalid, illegal, or
        unenforceable, the remaining provisions shall remain in full force and
        effect.
      </p>

      <h3 className="mt-6 text-base font-semibold">21. No Waiver</h3>
      <p className="text-sm leading-relaxed">
        No failure or delay by Company in exercising any right, power, or remedy
        under this Agreement shall operate as a waiver of that right, power, or
        remedy. Any waiver must be in writing and signed by an authorized
        representative of Company. A waiver of any provision shall not
        constitute a waiver of any other provision or of the same provision on a
        future occasion.
      </p>

      <h3 className="mt-6 text-base font-semibold">22. Entire Agreement</h3>
      <p className="text-sm leading-relaxed">
        This Agreement and the Privacy Policy constitute the entire agreement
        between you and Company.
      </p>

      <h3 className="mt-6 text-base font-semibold">Contact</h3>
      <p className="text-sm leading-relaxed">
        If you have questions about this Agreement, please contact us at{" "}
        <a href="mailto:support@c2vault.com" className="text-primary underline">
          support@c2vault.com
        </a>
        .
      </p>
    </div>
  );
}
